import React, {useContext, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import {InvoiceService, InvoiceProduct} from '../../../../../services/invoiceService';
import ProductDialog from './ProductDialog';

// Format currency
const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

// Format percentage
const formatPercent = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value / 100);
};

const ProductsTab: React.FC = () => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [products, setProducts] = useState<InvoiceProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editProduct, setEditProduct] = useState<InvoiceProduct | null>(null);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = () => {
        setLoading(true);
        setError(null);
        call_backend_api(
            InvoiceService.getProducts(false), // Include inactive
            (data: InvoiceProduct[]) => {
                setProducts(data);
                setLoading(false);
            },
            (err: any) => {
                console.error('Error loading products:', err);
                setError('Fehler beim Laden der Produkte');
                setLoading(false);
            },
            undefined,
            false
        );
    };

    const handleAdd = () => {
        setEditProduct(null);
        setDialogOpen(true);
    };

    const handleEdit = (product: InvoiceProduct) => {
        setEditProduct(product);
        setDialogOpen(true);
    };

    const handleDelete = (product: InvoiceProduct) => {
        if (!window.confirm(`Möchten Sie "${product.name}" wirklich deaktivieren?`)) {
            return;
        }
        call_backend_api(
            InvoiceService.deleteProduct(product.id),
            () => {
                loadProducts();
            },
            (err: any) => {
                console.error('Error deleting product:', err);
                setError('Fehler beim Deaktivieren des Produkts');
            },
            undefined,
            false
        );
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditProduct(null);
    };

    const handleDialogSave = () => {
        setDialogOpen(false);
        setEditProduct(null);
        loadProducts();
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                <Typography variant="h6">
                    Produkte verwalten
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Neu
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {products.length === 0 ? (
                <Box sx={{py: 4, textAlign: 'center'}}>
                    <Typography variant="body1" color="text.secondary">
                        Noch keine Produkte vorhanden.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Einheit</TableCell>
                                <TableCell align="right">Standardpreis</TableCell>
                                <TableCell align="right">MwSt.</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((product) => (
                                <TableRow key={product.id} hover>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">{product.name}</Typography>
                                            {product.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {product.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{product.unit_label || product.unit_code}</TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(product.default_price)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatPercent(product.vat_rate)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={product.active ? 'Aktiv' : 'Inaktiv'}
                                            color={product.active ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Bearbeiten">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(product)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Deaktivieren">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(product)}
                                                disabled={!product.active}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ProductDialog
                open={dialogOpen}
                product={editProduct}
                onClose={handleDialogClose}
                onSave={handleDialogSave}
            />
        </Box>
    );
};

export default ProductsTab;
